import { useState, useEffect, useCallback } from 'react';
import { api, getApiErrorMessage } from '../services/api';

export interface Topic {
  id: number;
  title: string;
  description: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface TopicWithCounts extends Topic {
  wordCount: number;
  quizCount: number;
}

export function useTopics() {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTopics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/topics');
      setTopics(res.data.data);
      setError(null);
    } catch (err: unknown) {
      // Without this the failed request renders as "пока нет тем".
      setError(getApiErrorMessage(err, 'Не удалось загрузить список тем'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

  const createTopic = async (data: { title: string; description?: string }) => {
    const res = await api.post('/topics', data);
    setTopics((prev) => [...prev, res.data.data]);
    return res.data.data;
  };

  const updateTopic = async (id: number, data: Partial<{ title: string; description: string }>) => {
    const res = await api.patch(`/topics/${id}`, data);
    setTopics((prev) => prev.map((t) => (t.id === id ? res.data.data : t)));
    return res.data.data;
  };

  const deleteTopic = async (id: number) => {
    await api.delete(`/topics/${id}`);
    setTopics((prev) => prev.filter((t) => t.id !== id));
  };

  return { topics, loading, error, fetchTopics, createTopic, updateTopic, deleteTopic };
}

export function useTopic(id: number | undefined) {
  const [topic, setTopic] = useState<TopicWithCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTopic = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await api.get(`/topics/${id}`);
      setTopic(res.data.data);
      setError(null);
    } catch (err: unknown) {
      // Ownership guards answer 404, so this also covers "not your topic".
      setError(getApiErrorMessage(err, 'Не удалось загрузить тему'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTopic();
  }, [fetchTopic]);

  return { topic, loading, error, fetchTopic };
}